import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import Reveal from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import lauraImage from "@/assets/laura.webp";

// Tappe della formazione, ricavate dalla biografia di Laura.
const timeline: { year: string; title: string; place?: string; note?: string }[] = [
  {
    year: "2020",
    title: "Diploma in Cristalloterapia",
    place: "Atman Veda Accademy",
    note: "Dalla passione per gemme e pietre preziose alla convinzione del loro potere curativo.",
  },
  {
    year: "2021",
    title: "Diploma di Operatrice Ayurvedica",
    place: "Atman Veda Accademy",
    note: "L'incontro con l'Ayurveda, la “scienza di vita” che riunisce i vari pezzi del puzzle.",
  },
  {
    year: "2021",
    title: "Diploma in Terapie Sonore con Campane Tibetane",
    place: "Scuola “Epoka” di Francesco Vittadello",
    note: "Un percorso nato dallo studio del pianoforte, della chitarra, del flauto e di strumenti ancestrali.",
  },
  {
    year: "2022",
    title: "Nasce ORIGINI",
    note: "Lascia un lavoro sicuro e il tiro con l'arco per dedicarsi completamente all'Ayurveda, aprendo uno studio tutto suo.",
  },
  {
    year: "2023",
    title: "Diploma nel massaggio infantile “Shantala”",
    place: "con Barbara Fregnan",
    note: "Un ampliamento della formazione ayurvedica dedicato ai più piccoli.",
  },
  {
    year: "2024",
    title: "Inizio del percorso in Riflessologia Plantare",
    place: "Associazione AFOS — Firenze",
    note: "Nella tradizione della Medicina Tradizionale Cinese, con le competenze della scuola Zu Center di Milano.",
  },
  {
    year: "2025",
    title: "Diploma CSEN di Insegnante di Ayurveda tradizionale",
    place: "Atman Veda Accademy",
    note: "Dopo quattro anni come docente al Corso di Operatore Ayurvedico.",
  },
  {
    year: "2027",
    title: "Massimo diploma CSEN in Riflessologia Plantare",
    note: "Il percorso attualmente in corso, che completerà la sua formazione riflessologica.",
  },
];

const Laura = () => {
  return (
    <Layout>
      <SEOHead
        title="Laura Casarella — La mia storia | Studio Olistico ORIGINI"
        description="La storia di Laura Casarella, operatrice olistica e insegnante di Ayurveda tradizionale: dalla filosofia all'Ayurveda, dalla cristalloterapia alla riflessologia plantare. Il percorso dietro ORIGINI."
        url="/laura"
        type="profile"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          mainEntity: {
            "@type": "Person",
            name: "Laura Casarella",
            jobTitle: "Operatrice Olistica e Insegnante di Ayurveda tradizionale",
            url: "https://origini.cloud/laura",
            worksFor: {
              "@type": "HealthAndBeautyBusiness",
              name: "ORIGINI Studio Olistico",
              url: "https://origini.cloud",
            },
            alumniOf: "Atman Veda Accademy",
            knowsAbout: [
              "Ayurveda",
              "Riflessologia plantare",
              "Cristalloterapia",
              "Terapia del suono con campane tibetane",
              "Massaggio ayurvedico",
              "Massaggio infantile Shantala",
            ],
          },
        }}
      />

      {/* Hero: foto + presentazione */}
      <section className="bg-gradient-to-b from-secondary/40 to-background">
        <div className="container mx-auto px-4 pt-28 pb-16 md:pt-32 md:pb-20">
          <div className="grid md:grid-cols-5 gap-10 md:gap-14 items-center max-w-5xl mx-auto">
            <Reveal className="md:col-span-2 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-3 rounded-full bg-accent/20 blur-lg" />
                <img
                  src={lauraImage}
                  alt="Laura Casarella — Operatrice Olistica e Insegnante di Ayurveda"
                  width={288}
                  height={288}
                  className="relative w-64 h-64 md:w-72 md:h-72 object-cover rounded-full shadow-xl ring-4 ring-card"
                />
              </div>
            </Reveal>

            <Reveal className="md:col-span-3 text-center md:text-left">
              <p className="eyebrow">La mia storia</p>
              <h1 className="mt-3 font-serif text-5xl md:text-7xl text-primary leading-[0.95]">
                Laura
              </h1>
              <p className="mt-4 font-serif italic text-xl md:text-2xl text-accent">
                Operatrice Olistica · Insegnante di Ayurveda tradizionale
              </p>
              <p className="mt-6 text-lg text-foreground/80 leading-relaxed">
                Un percorso che parte dalla filosofia e dalla ricerca dell'essenza di ogni
                cosa, e trova nell'Ayurveda e nelle discipline olistiche la sua strada.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Biografia */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <Reveal className="space-y-6 text-lg text-foreground/90 leading-relaxed">
            <p>
              Laureata con lode in Filosofia Morale, nello specifico “filosofia delle
              religioni”, da sempre sono rimasta affascinata da come l'uomo, nelle diverse
              culture, abbia dato importanza al rapporto col “divino”, raggiungendo l'apice
              proprio nella civiltà indiana, laddove umano e divino quasi si fondono in
              quella costante ricerca di evoluzione che parte dalla profonda conoscenza del
              “sé” e del proprio “ben-essere”, e porta al ricongiungimento del divino che è
              in ognuno di noi con l'Uno.
            </p>
            <p>
              Spontanea, verace, in continuo movimento e divenire, ricerco da sempre
              l'essenza di ogni cosa, il vero “io” di ogni persona che incontro, convinta
              che l'empatia sia l'unico modo per instaurare la giusta relazione fra gli
              esseri viventi.
            </p>
            <p>
              Dopo anni di continue esperienze sia personali che lavorative, che mi hanno
              permesso di sviluppare discrete doti comunicative e di ampliare le varie
              conoscenze dell'ampio mondo olistico, ho trovato nell'Ayurveda quella
              “scienza di vita” in grado di radunare e completare i vari pezzi del puzzle.
            </p>
            <p>
              Acquisito nel 2021 il diploma di Operatrice Ayurvedica presso Atman Veda
              Accademy, nel 2022 ho abbandonato un lavoro sicuro ed uno sport — il tiro con
              l'arco — che mi ha regalato grandi soddisfazioni, per dedicarmi completamente
              all'Ayurveda e al mondo olistico che la circonda, aprendo uno studio tutto mio
              dove opero quotidianamente per portare equilibrio e benessere alle persone che
              si affidano a me.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Formazione & percorso */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-background to-secondary/30">
        <div className="container mx-auto px-4 max-w-3xl">
          <Reveal className="text-center mb-14">
            <p className="eyebrow">Formazione &amp; percorso</p>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-primary">
              Un cammino in continua evoluzione
            </h2>
            <div className="w-20 h-0.5 bg-accent mx-auto rounded-full mt-5" />
          </Reveal>

          <ol className="relative border-l-2 border-accent/25 ml-3 space-y-10">
            {timeline.map((t, i) => (
              <Reveal key={i} delay={(i % 4) * 60}>
                <li className="relative pl-8">
                  <span className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-accent ring-4 ring-background" />
                  <span className="inline-block font-serif text-2xl text-accent leading-none">
                    {t.year}
                  </span>
                  <h3 className="mt-1 font-serif text-xl md:text-2xl text-primary">
                    {t.title}
                  </h3>
                  {t.place && (
                    <p className="text-sm uppercase tracking-wide text-muted-foreground mt-1">
                      {t.place}
                    </p>
                  )}
                  {t.note && (
                    <p className="mt-2 text-foreground/80 leading-relaxed">{t.note}</p>
                  )}
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Chiusura */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <Reveal>
            <blockquote className="font-serif text-2xl md:text-4xl text-primary leading-tight">
              Ogni tecnica volta a portare benessere alle persone può dare il proprio
              <span className="italic text-accent"> contributo, specifico ed efficace.</span>
            </blockquote>
            <p className="mt-6 text-lg text-foreground/80 leading-relaxed">
              Ad oggi i miei studi sono in continua evoluzione: se nell'Ayurveda ho trovato
              una scienza davvero completa, rimango pur sempre convinta che ogni disciplina
              esistente possa contribuire al benessere della persona nella sua interezza.
            </p>

            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="text-base px-8 py-6 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground"
              >
                <a href="https://wa.me/393461533000" target="_blank" rel="noopener noreferrer">
                  Prenota una consulenza
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="text-base px-8 py-6 rounded-full"
              >
                <Link to="/trattamenti">Scopri i trattamenti</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
};

export default Laura;
